import { z } from "zod";
import { DateTimeWithOffset, FalsyBool } from "@/lib/api/schemas/patterns";
import { RatioValue } from "@/lib/api/schemas/shared";
import { Auth } from "../auth";
import type { ActionReturnType } from "../types";
import { KY_OPTIONS } from "@/config";
import { apiClient as ky } from "../kyConfig";
import { logApi } from "../logger";
import { request, validateInput } from "../request";
import { AppResetMode, CaseAppResetMode, TestPayload } from "@/lib/api/schemas/suite";

export type SuitesReadResponse = z.infer<typeof SuitesEndpoint.schemas.read.response>;

class SuitesEndpoint {
  public static readonly basePath = "suites";
  public static readonly schemas = {
    list: {
      response: z.object({
        count: z.number(),
        items: z
          .object({
            id: z.number(),
            created_at: DateTimeWithOffset,
            modified_at: DateTimeWithOffset,
            name: z.string(),
            project_id: z.number(),
            is_playground: FalsyBool,
            cases_count: z.number().nullish(),
            /** Success over the cycles that reached a verdict, whole history, subsets rolled
             *  up into the suite they were cut from. `null` — not `0` — when nothing of this
             *  suite has a verdict yet. */
            pass_rate: RatioValue.nullish(),
            cases: z
              .object({
                id: z.number(),
                created_at: DateTimeWithOffset,
                name: z.string(),
                payload: TestPayload,
              })
              .array()
              .nullable(),
          })
          .array(),
      }),
    },
    create: {
      body: z.object({
        name: z.string(),
        is_playground: z.boolean().default(false).optional(),
      }),
    },
    read: {
      response: z.object({
        id: z.number(),
        created_at: DateTimeWithOffset,
        modified_at: DateTimeWithOffset,
        name: z.string(),
        project_id: z.number(),
        is_playground: FalsyBool,
        /** What the runner does to the app between this suite's cases — the default
         *  every run of it inherits unless the run overrides it. */
        app_reset_mode: AppResetMode,
        /** Success over the cycles that reached a verdict, whole history, subsets rolled
         *  up into the suite they were cut from. `null` — not `0` — when nothing of this
         *  suite has a verdict yet. */
        pass_rate: RatioValue.nullish(),
        cases: z
          .object({
            id: z.number(),
            order: z.number(),
            created_at: DateTimeWithOffset,
            /** Human-facing handle (e.g. `CFKIOS-1`) — unique per client, shared by every suite holding the case. */
            key: z.string(),
            name: z.string(),
            /** Version the suite points at: editing a case writes a new version and the suite follows it. */
            version: z.number(),
            /** When that version was written — a version row never changes once created. */
            modified_at: DateTimeWithOffset,
            /** This case's own say on what happens to the app before it runs; `inherit` leaves it to the suite. */
            app_reset_mode: CaseAppResetMode,
            payload: TestPayload,
          })
          .array()
          .nullable(),
      }),
    },
    update: {
      // A full PUT: the backend replaces the suite with what it is handed, so
      // every field the caller means to keep has to be in the body.
      body: z.object({
        name: z.string(),
        app_reset_mode: AppResetMode.optional(),
      }),
    },
    reorder: {
      /** Every case of the suite exactly once — a partial list is a 400 naming what is off. */
      body: z.object({
        case_ids: z.number().array(),
      }),
      response: z.object({
        suite_id: z.number(),
        case_ids: z.number().array(),
      }),
    },
    import: {
      response: z.object({
        created_suites: z.number().array(),
      }),
    },
  };

  constructor(private api: Auth) {}

  public list(query?: {
    search?: string;
    ordering?: string;
    limit?: number;
    offset?: number;
    project_id?: string;
    is_playground?: boolean;
  }): ActionReturnType<z.infer<typeof SuitesEndpoint.schemas.list.response>> {
    return request({
      log: ["Suites", "list"],
      method: "get",
      path: [SuitesEndpoint.basePath],
      query,
      response: SuitesEndpoint.schemas.list.response,
    });
  }

  public async create(
    body: z.input<typeof SuitesEndpoint.schemas.create.body>,
    query: { project_id: string },
  ): ActionReturnType<undefined> {
    const bodyValidated = validateInput(SuitesEndpoint.schemas.create.body, body);
    if (!bodyValidated.ok) return bodyValidated;
    return request({
      log: ["Suites", "create"],
      method: "post",
      path: [SuitesEndpoint.basePath],
      query,
      json: bodyValidated.data,
    });
  }

  public read(id: string | number): ActionReturnType<z.infer<typeof SuitesEndpoint.schemas.read.response>> {
    return request({
      log: ["Suites", "read"],
      method: "get",
      path: [SuitesEndpoint.basePath, id],
      response: SuitesEndpoint.schemas.read.response,
    });
  }

  public async update(
    id: string,
    body: z.infer<typeof SuitesEndpoint.schemas.update.body>,
  ): ActionReturnType<undefined> {
    const bodyValidated = validateInput(SuitesEndpoint.schemas.update.body, body);
    if (!bodyValidated.ok) return bodyValidated;
    return request({
      log: ["Suites", "update"],
      method: "put",
      path: [SuitesEndpoint.basePath, id],
      json: bodyValidated.data,
    });
  }

  public delete(id: string): ActionReturnType<undefined> {
    return request({
      log: ["Suites", "delete"],
      method: "delete",
      path: [SuitesEndpoint.basePath, id],
    });
  }

  public async reorder(
    id: string,
    body: z.infer<typeof SuitesEndpoint.schemas.reorder.body>,
  ): ActionReturnType<z.infer<typeof SuitesEndpoint.schemas.reorder.response>> {
    const bodyValidated = validateInput(SuitesEndpoint.schemas.reorder.body, body);
    if (!bodyValidated.ok) return bodyValidated;
    return request({
      log: ["Suites", "reorder"],
      method: "put",
      path: [SuitesEndpoint.basePath, id, "order"],
      json: bodyValidated.data,
      response: SuitesEndpoint.schemas.reorder.response,
      localErrorHandling: true,
    });
  }

  public addTestToSuite(suiteId: string, testId: string): ActionReturnType<undefined> {
    return request({
      log: ["Suites", "addTestToSuite"],
      method: "post",
      path: [SuitesEndpoint.basePath, suiteId, testId],
    });
  }

  public removeTestFromSuite(suiteId: string, testId: string | number): ActionReturnType<undefined> {
    return request({
      log: ["Suites", "removeTestFromSuite"],
      method: "delete",
      path: [SuitesEndpoint.basePath, suiteId, testId],
    });
  }

  public async export(query: {
    project_id: string;
    suite_ids?: number[];
    /** The backend's default is 2; see `exportVersionFor` for when 1 is needed. */
    version?: 1 | 2;
  }): ActionReturnType<{ blob: Blob; filename: string | null }> {
    const params = new URLSearchParams({
      project_id: query.project_id,
    });
    if (query.version) params.append("version", String(query.version));
    query.suite_ids?.forEach((suiteId) => {
      params.append("suite_ids", suiteId.toString());
    });

    const url = `${this.api.apiUrl([SuitesEndpoint.basePath, "export"])}?${params.toString()}`;

    let blob: Blob;
    let filename: string | null = null;
    try {
      const response = await ky.get(url, {
        headers: this.api.getAuthHeader(),
        ...KY_OPTIONS,
      });

      blob = await response.blob();

      const contentDisposition = response.headers.get("content-disposition") ?? "";
      const encodedNameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
      const plainNameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);

      if (encodedNameMatch?.[1]) {
        filename = decodeURIComponent(encodedNameMatch[1]);
      } else if (plainNameMatch?.[1]) {
        filename = plainNameMatch[1];
      }
    } catch (error) {
      return {
        ok: false,
        type: "request",
        error: error instanceof Error ? error.message : "An unknown error occurred",
      };
    }

    return {
      ok: true,
      data: {
        blob,
        filename,
      },
    };
  }

  public async import(
    file: File,
    query: { project_id: string },
  ): ActionReturnType<z.infer<typeof SuitesEndpoint.schemas.import.response>> {
    const formData = new FormData();
    formData.append("file", file);

    let data: any;
    try {
      data = await ky
        .post(this.api.apiUrl([SuitesEndpoint.basePath, "import"], { project_id: query.project_id }), {
          body: formData,
          headers: this.api.getAuthHeader(),
          ...KY_OPTIONS,
        })
        .json();
    } catch (error: any) {
      let errorResponse: any = null;
      if (error?.response) {
        try {
          errorResponse = await error.response.clone().json();
        } catch {
          try {
            errorResponse = await error.response.clone().text();
          } catch {}
        }
      }

      if (errorResponse?.detail) {
        const errorMsg = Array.isArray(errorResponse.detail)
          ? errorResponse.detail.map((d: any) => d.msg || JSON.stringify(d)).join("; ")
          : errorResponse.detail;
        return {
          ok: false,
          type: "request",
          error: errorMsg,
        };
      }

      return {
        ok: false,
        type: "request",
        error: error instanceof Error ? error.message : "An unknown error occurred",
      };
    }

    logApi("Suites", "import", "response", data);
    const parsedData = SuitesEndpoint.schemas.import.response.safeParse(data);
    logApi("Suites", "import", "parsed", parsedData);

    if (!parsedData.success) {
      return {
        ok: false,
        type: "response_validation",
        error: parsedData.error.message,
      };
    }

    return {
      ok: true,
      data: parsedData.data,
    };
  }
}

export { SuitesEndpoint };
